/**
 * The face a food wears, and the deck a meal or a recipe wears — see the nutrition spec, Face
 * and Deck. The table they are read off, and the icons it names, are `@gymcloud/food-marks`:
 * the same table the Android app carries, so a food wears one face on both. Nothing chooses a
 * face: an emoji a food, a meal or an entry's copy of a food still carries from when one could
 * be chosen is never read.
 *
 * A face is an emoji and, where one has been drawn, an icon. The icon is what a food's mark
 * shows; the emoji is what is said wherever a face is written out as text, as in a shared food.
 */

import { type Face, PLAIN_FACE, type Part, deckOf, faceForName } from '@gymcloud/food-marks'

import { type Ingredient, type Meal, type Recipe, membersOn } from '~/data/nutrition'

export { type Face, faceForName }

/** What a food whose name calls for nothing wears. */
export const PLAIN_FOOD_EMOJI = PLAIN_FACE.emoji

/** What a deck reads off one of the things a meal or a recipe is made of. */
function partOf(ingredient: Ingredient): Part {
  return { name: ingredient.name, calories: ingredient.nutrients.calories }
}

/** The faces a food wears: a recipe's deck, and a single face for anything else. */
export function foodDeck(food: {
  readonly name: string
  readonly recipe?: Recipe | null
}): readonly Face[] {
  const recipe = food.recipe ?? null
  return recipe === null
    ? [faceForName(food.name)]
    : deckOf(food.name, recipe.ingredients.map(partOf))
}

/** The deck a meal wears, off its name and the slots that are on. */
export function mealDeck(meal: Pick<Meal, 'name' | 'slots' | 'state'>): readonly Face[] {
  return deckOf(meal.name, membersOn(meal.slots, meal.state).map(partOf))
}

/** The emoji a food wears, for wherever it is written out as text: its deck's front face's. */
export function foodEmoji(food: {
  readonly name: string
  readonly recipe?: Recipe | null
}): string {
  return foodDeck(food)[0]?.emoji ?? PLAIN_FOOD_EMOJI
}

/** The emoji a meal wears, for wherever it is written out as text. */
export function mealEmoji(meal: Pick<Meal, 'name' | 'slots' | 'state'>): string {
  return mealDeck(meal)[0]?.emoji ?? PLAIN_FOOD_EMOJI
}

/** The same, off a name alone — for a quick add's own line. */
export function emojiForName(name: string): string {
  return faceForName(name).emoji
}

const ICONS = import.meta.glob<string>('../../../../packages/food-marks/assets/food-icons/*.webp', {
  eager: true,
  // Each icon a file of its own, never inlined into the bundle every page loads.
  query: '?url&no-inline',
  import: 'default',
})

/** Where an icon is served from, by its name in the table. */
export function iconUrl(icon: string): string | undefined {
  return ICONS[`../../../../packages/food-marks/assets/food-icons/${icon}.webp`]
}
